import React, { createContext, Fragment, useContext, useEffect, useState } from 'react';
import type { DragEndEvent, DragOverEvent, UniqueIdentifier } from '@dnd-kit/core';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import { Button, Card, Checkbox, Col, Flex, Row, Table } from 'antd';
import type { TableColumnsType } from 'antd';
import { mdlReservation } from '../../models/domain/Reservation';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store/Hooks';
import { setResMonColumns } from '../../store/SiteSlice';
import _ from 'lodash';


interface HeaderCellProps extends React.HTMLAttributes<HTMLTableCellElement> {
  id: string;
}

interface BodyCellProps extends React.HTMLAttributes<HTMLTableCellElement> {
  id: string;
}

interface DragIndexState {
  active: UniqueIdentifier;
  over: UniqueIdentifier | undefined;
  direction?: 'left' | 'right';
}

const DragIndexContext = createContext<DragIndexState>({ active: -1, over: -1 });

const dragActiveStyle = (dragState: DragIndexState, id: string) => {
  const { active, over, direction } = dragState;
  let style: React.CSSProperties = {};
  if (active && active === id) {
    style = { backgroundColor: 'gray', opacity: 0.5 };
  }
  else if (over && id === over && active !== over) {
    style =
      direction === 'right'
        ? { borderRight: '1px dashed gray' }
        : { borderLeft: '1px dashed gray' };
  }
  return style;
};

const TableBodyCell: React.FC<BodyCellProps> = (props) => {
  const dragState = useContext<DragIndexState>(DragIndexContext);
  return <td {...props} style={{ ...props.style, ...dragActiveStyle(dragState, props.id) }} />;
};

const TableHeaderCell: React.FC<HeaderCellProps> = (props) => {
  const dragState = useContext(DragIndexContext);
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({ id: props.id });
  const style: React.CSSProperties = {
    ...props.style,
    cursor: 'move',
    ...(isDragging ? { position: 'relative', zIndex: 9999, userSelect: 'none' } : {}),
    ...dragActiveStyle(dragState, props.id),
  };
  return <th {...props} ref={setNodeRef} style={style} {...attributes} {...listeners} />;
};

export type ResMonSortColumnTableProps = {
  callBack?: Function;
}

export const ResMonSortColumnTable = (props: ResMonSortColumnTableProps) => {
  const [dragIndex, setDragIndex] = useState<DragIndexState>({ active: -1, over: -1 });
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const cookieColumns = useAppSelector(x => x.site.resMonColumns);

  const checkBoxOptions = [
    { label: t("resTable.hotelConfirmationStatus"), value: 'confirmationStatus' },
    { label: t("resTable.reservationType"), value: 'reservationType' },
    { label: t("resTable.reservationStatus"), value: 'reservationStatus' },
    { label: t("resTable.agencyName"), value: 'agencyName' },
    { label: t("resTable.hotelName"), value: 'hotelName' },
    { label: t("resTable.sejHotelName"), value: 'sejHotelName' },
    { label: t("resTable.operator"), value: 'operator' },
    { label: t("resTable.voucherNo"), value: 'voucherNo' },
    { label: t("resTable.sellDate"), value: 'sellDate' },
    { label: t("resTable.confirmDate"), value: 'confirmDate' },
    { label: t("resTable.confirmeNote"), value: 'confirmeNote' },
    { label: t("resTable.checkIn"), value: 'checkIn' },
    { label: t("resTable.checkOut"), value: 'checkOut' },
    { label: t("resTable.nights"), value: 'nights' },
    { label: t("resTable.roomName"), value: 'roomName' },
    { label: t("resTable.roomTypeName"), value: 'roomTypeName' },
    { label: t("resTable.accomName"), value: 'accomName' },
    { label: t("resTable.roomCount"), value: 'roomCount' },
    { label: t("resTable.quotaType"), value: 'quotaType' },
    { label: t("resTable.soldPrice"), value: 'soldPrice' },
    { label: t("resTable.soldCurrency"), value: 'soldCurrency' },
    { label: t("resTable.hasExtras"), value: 'hasExtra' },
    { label: t("resTable.originalHotelCode"), value: 'originalHotelCode' },
    { label: t("resTable.adults"), value: 'adults' },
    { label: t("resTable.childs"), value: 'childs' },
    { label: t("resTable.infants"), value: 'infants' },
    { label: t("resTable.extraBed"), value: 'extraBed' },
    { label: t("resTable.pax"), value: 'pax' },
    { label: t("resTable.note"), value: 'note' },
  ];
  const [visibleColumns, setVisibleColumns] = useState<Array<string>>(cookieColumns ? cookieColumns : checkBoxOptions.map(x => x.value));

  const baseColumns: TableColumnsType<mdlReservation> = [
    { title: t("resTable.hotelConfirmationStatus"), dataIndex: 'confirmationStatus', hidden: !visibleColumns.includes("confirmationStatus") },
    { title: t("resTable.reservationType"), dataIndex: 'reservationType', hidden: !visibleColumns.includes("reservationType") },
    { title: t("resTable.reservationStatus"), dataIndex: 'reservationStatus', hidden: !visibleColumns.includes("reservationStatus") },
    { title: t("resTable.agencyName"), dataIndex: 'agencyName', hidden: !visibleColumns.includes("agencyName") },
    { title: t("resTable.hotelName"), dataIndex: 'hotelName', hidden: !visibleColumns.includes("hotelName") },
    { title: t("resTable.sejHotelName"), dataIndex: 'sejHotelName', hidden: !visibleColumns.includes("sejHotelName") },
    { title: t("resTable.operator"), dataIndex: 'operator', hidden: !visibleColumns.includes("operator") },
    { title: t("resTable.voucherNo"), dataIndex: 'voucherNo', hidden: !visibleColumns.includes("voucherNo") },
    { title: t("resTable.sellDate"), dataIndex: 'sellDate', hidden: !visibleColumns.includes("sellDate") },
    { title: t("resTable.confirmDate"), dataIndex: 'confirmDate', hidden: !visibleColumns.includes("confirmDate") },
    { title: t("resTable.confirmeNote"), dataIndex: 'confirmeNote', hidden: !visibleColumns.includes("confirmeNote") },
    { title: t("resTable.checkIn"), dataIndex: 'checkIn', hidden: !visibleColumns.includes("checkIn") },
    { title: t("resTable.checkOut"), dataIndex: 'checkOut', hidden: !visibleColumns.includes("checkOut") },
    { title: t("resTable.nights"), dataIndex: 'nights', hidden: !visibleColumns.includes("nights") },
    { title: t("resTable.roomName"), dataIndex: 'roomName', hidden: !visibleColumns.includes("roomName") },
    { title: t("resTable.roomTypeName"), dataIndex: 'roomTypeName', hidden: !visibleColumns.includes("roomTypeName") },
    { title: t("resTable.accomName"), dataIndex: 'accomName', hidden: !visibleColumns.includes("accomName") },
    { title: t("resTable.roomCount"), dataIndex: 'roomCount', hidden: !visibleColumns.includes("roomCount") },
    { title: t("resTable.quotaType"), dataIndex: 'quotaType', hidden: !visibleColumns.includes("quotaType") },
    { title: t("resTable.soldPrice"), dataIndex: 'soldPrice', hidden: !visibleColumns.includes("soldPrice") },
    { title: t("resTable.soldCurrency"), dataIndex: 'soldCurrency', hidden: !visibleColumns.includes("soldCurrency") },
    { title: t("resTable.hasExtras"), dataIndex: 'hasExtra', hidden: !visibleColumns.includes("hasExtra") },
    { title: t("resTable.originalHotelCode"), dataIndex: 'originalHotelCode', hidden: !visibleColumns.includes("originalHotelCode") },
    { title: t("resTable.adults"), dataIndex: 'adults', hidden: !visibleColumns.includes("adults") },
    { title: t("resTable.childs"), dataIndex: 'childs', hidden: !visibleColumns.includes("childs") },
    { title: t("resTable.infants"), dataIndex: 'infants', hidden: !visibleColumns.includes("infants") },
    { title: t("resTable.extraBed"), dataIndex: 'extraBed', hidden: !visibleColumns.includes("extraBed") },
    { title: t("resTable.pax"), dataIndex: 'pax', hidden: !visibleColumns.includes("pax") },
    { title: t("resTable.note"), dataIndex: 'note', hidden: !visibleColumns.includes("note") },
  ];

  const reorderColumns = (columns: TableColumnsType<mdlReservation>, order?: string[]) => {
    if (order) {
      const orderedColumns: TableColumnsType<mdlReservation> = [];
      const remainingColumns: TableColumnsType<mdlReservation> = [];

      // Create a map for quick lookup
      const orderMap = new Map(order.map((key, index) => [key, index]));

      // Separate ordered and remaining columns
      columns.forEach((col) => {
        if (orderMap.has((col as any).dataIndex as string)) {
          orderedColumns[orderMap.get((col as any).dataIndex as string)!] = col;
        } else {
          remainingColumns.push(col);
        }
      });

      // Combine ordered columns and remaining columns
      return [...orderedColumns.filter(Boolean), ...remainingColumns];
    }
    else {
      return columns;
    }
  };

  const orderedColumns = reorderColumns(baseColumns, cookieColumns);

  const [columns, setColumns] = useState(() => orderedColumns.map((column, i) => ({
    ...column,
    key: `${i}`,
    onHeaderCell: () => ({ id: `${i}` }),
    onCell: () => ({ id: `${i}` }),
  })));

  useEffect(() => {
    var res =
      orderedColumns.map((column, i) => ({
        ...column,
        key: `${i}`,
        onHeaderCell: () => ({ id: `${i}` }),
        onCell: () => ({ id: `${i}` }),
      }));
    setColumns(res);
  }, [visibleColumns, cookieColumns]);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    }),
  );

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setColumns((prevState) => {
        const activeIndex = prevState.findIndex((i) => i.key === active?.id);
        const overIndex = prevState.findIndex((i) => i.key === over?.id);
        return arrayMove(prevState, activeIndex, overIndex);
      });
    }
    setDragIndex({ active: -1, over: -1 });
  };

  const onDragOver = ({ active, over }: DragOverEvent) => {
    const activeIndex = columns.findIndex((i) => i.key === active.id);
    const overIndex = columns.findIndex((i) => i.key === over?.id);
    setDragIndex({
      active: active.id,
      over: over?.id,
      direction: overIndex > activeIndex ? 'right' : 'left',
    });
  };

  const handleSave = () => {
    const reorderedColumns = Array.from(columns);
    var visibleColumns = _.filter(reorderedColumns, function (r) { return r.hidden == false }).map(x => (x as any).dataIndex);
    dispatch(setResMonColumns(visibleColumns));
    props.callBack && props.callBack();
  }


  const onChange = (list: string[]) => {
    setVisibleColumns(list);
  };



  return (
    <Fragment>
      <Card>
        <Flex justify='center' style={{ marginBottom: "25px" }}>
          {t("resTable.selectableColumns")}
        </Flex>
        <Flex justify='center'>
          <Row>
            <Col>
              <Checkbox.Group options={checkBoxOptions} defaultValue={visibleColumns} onChange={onChange} />
            </Col>
          </Row>
        </Flex>
      </Card>
      <Card>
        <Flex justify='center' style={{ marginBottom: "25px" }}>
          {t("resTable.tableView")}
        </Flex>
        <DndContext
          sensors={sensors}
          modifiers={[restrictToHorizontalAxis]}
          onDragEnd={onDragEnd}
          onDragOver={onDragOver}
          collisionDetection={closestCenter}
        >
          <SortableContext items={columns.map((i) => i.key)} strategy={horizontalListSortingStrategy}>
            <DragIndexContext.Provider value={dragIndex}>
              <Table<mdlReservation>
                rowKey="key"
                columns={columns}
                dataSource={[]}
                components={{
                  header: { cell: TableHeaderCell },
                  body: { cell: TableBodyCell },
                }}
              />
            </DragIndexContext.Provider>
          </SortableContext>
          <DragOverlay>
            <div style={{ backgroundColor: 'gray', padding: 16 }}>
              {columns[columns.findIndex((i) => i.key === dragIndex.active)]?.title as React.ReactNode}
            </div>
          </DragOverlay>
        </DndContext>
      </Card>
      <Card>
        <Flex justify='end'>
          <Button onClick={handleSave} type="primary" >{t("common.save")}</Button>
        </Flex>
      </Card>
    </Fragment>
  );
};